// web-v2/src/lib/stateTax.js
//
// The state tax suggestion, as a pure function. Given the airport the server
// resolved, the rate row for its state, and the money currently in the form, it
// returns either an amount or the reason there is not one.
//
// IT SUGGESTS. IT NEVER WRITES. The wizard renders the amount behind a chip the
// broker clicks, and the value lands marked as defaulted, exactly the shape the
// per-aircraft premium sum already uses. A territory default is wrong on screen
// and gets corrected; a tax default is wrong on an invoice and gets paid.
//
// EVERY REFUSAL SAYS WHY, IN THE WORDS THE BROKER NEEDS TO FIX IT. "No confirmed
// rate on file for WV" is actionable. A blank field is not, and a $0 is worse
// than either, because $0 of surplus lines tax is a claim.
//
// The rate LOOKUP is not here. The server resolves airport -> state (through
// airports.iso_region, deterministically, never from an address blob) and reads
// state_tax_rates. This module does the arithmetic and the refusing, so both can
// be tested in node without a database.

// The whole vocabulary. A basis this module cannot compute is treated as unset:
// it refuses rather than improvising, because improvising is the failure mode
// this feature exists to prevent.
export const TAX_BASES = ['premium', 'premium_plus_fees'];

export const BASIS_LABELS = {
  premium: 'premium',
  premium_plus_fees: 'premium plus fees',
};

// Money text or number -> number, or null. '12,345' and '$250' are what the
// form actually holds, so they are what this takes.
function money(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// A rate is a fraction: 0.01 is 1 percent. Rendered as a percentage because that
// is how a surplus lines rate is quoted and how David stated both of his.
export function formatRate(rate) {
  if (rate == null || !Number.isFinite(Number(rate))) return '';
  const pct = Number(rate) * 100;
  // Trim to the shortest exact form: 1% not 1.0000%, 0.55% not 0.5500%.
  return `${Number(pct.toFixed(4))}%`;
}

function blocked(reason, message, state = null) {
  return { blocked: true, reason, message, amount: null, state, rate: null, basis: null };
}

/**
 * @param {object}      input
 * @param {object|null} input.airport  What the server resolved: `{ ident, state }`.
 *                                     `state` null means the identifier did not
 *                                     resolve. The whole object null means no
 *                                     base airport was recorded at all, which is
 *                                     a different problem with a different fix.
 * @param {object|null} input.rateRow  The state_tax_rates row, or null when the
 *                                     state has none.
 * @param {number|string|null} input.premium
 * @param {number|string|null} input.fees
 * @returns {{blocked:boolean, reason:string, message:string, amount:number|null,
 *            state:string|null, rate:number|null, basis:string|null}}
 */
export function taxSuggestion({ airport, rateRow, premium, fees } = {}) {
  if (!airport || !String(airport.ident || '').trim()) {
    return blocked('no_airport',
      'No base airport on file for this aircraft, so there is no state to tax against.');
  }
  const ident = String(airport.ident).trim().toUpperCase();
  const state = String(airport.state || '').trim().toUpperCase() || null;
  if (!state) {
    // NAME THE AIRPORT. An operator cannot fix "the airport did not resolve".
    return blocked('airport_unresolved',
      `${ident} is not in the airport data, so the state is unknown and no tax is suggested.`);
  }

  const rate = rateRow && rateRow.rate != null ? Number(rateRow.rate) : null;
  if (rate == null || !Number.isFinite(rate)) {
    return blocked('no_rate', `No rate on file for ${state}.`, state);
  }

  // CONFIRMATION IS THE OUTER GATE. A fully specified rate nobody has verified is
  // the exact case this guards: West Virginia arrived as "may be 0.55%", and a
  // basis being present must not talk the caller past that.
  if (rateRow.confirmed !== true) {
    return blocked('unconfirmed', `No confirmed rate on file for ${state}.`, state);
  }

  const basis = TAX_BASES.includes(rateRow.basis) ? rateRow.basis : null;
  if (!basis) {
    return blocked('basis_unset',
      `${state} is set to ${formatRate(rate)}, but its basis is not set, so nothing is suggested. `
      + 'Tax on premium alone and tax on premium plus fees are different numbers.',
      state);
  }

  const p = money(premium);
  // A SUM OF NOTHING IS NOT ZERO, the same rule derivedTaxesAndFees holds. A
  // premium nobody has typed produces no figure rather than a $0 tax.
  if (p == null || p === 0) {
    return blocked('no_premium', 'Enter the premium and the tax will be offered here.', state);
  }
  const f = basis === 'premium_plus_fees' ? (money(fees) || 0) : 0;

  const amount = Math.round((p + f) * rate * 100) / 100;
  return {
    blocked: false,
    reason: 'ok',
    amount,
    state,
    rate,
    basis,
    message: `${state} ${formatRate(rate)} of ${BASIS_LABELS[basis]}`,
  };
}
